import React, { useState, useEffect, useRef } from "react";
import heroImage from './assets/images/hero.jpg';
import wolfLogo from './assets/images/wolf.png';

// Project images imports
import wolf1 from './assets/images/projects/wolf-1.jpeg';
import wolf2 from './assets/images/projects/wolf-2.jpeg';
import wolf3 from './assets/images/projects/wolf-3.jpeg';
import wolf4 from './assets/images/projects/wolf-4.jpeg';
import wolf5 from './assets/images/projects/wolf-5.jpeg';
import wolf6 from './assets/images/projects/wolf-6.jpeg';
import wolf7 from './assets/images/projects/wolf-7.jpeg';
import wolf8 from './assets/images/projects/wolf-8.jpeg';
import wolf9 from './assets/images/projects/wolf-9.jpeg';
import wolf10 from './assets/images/projects/wolf-10.jpeg';
import wolf11 from './assets/images/projects/wolf-11.jpeg';
import wolf15 from './assets/images/projects/wolf-15.jpeg';
import wolf20 from './assets/images/projects/wolf-20.jpeg';
import wolf25 from './assets/images/projects/wolf-25.jpeg';
import wolf30 from './assets/images/projects/wolf-30.jpeg';
import wolf35 from './assets/images/projects/wolf-35.jpeg';
import wolf40 from './assets/images/projects/wolf-40.jpeg';
import wolf45 from './assets/images/projects/wolf-45.jpeg';
import wolf55 from './assets/images/projects/wolf-55.jpeg';
import wolf60 from './assets/images/projects/wolf-60.jpeg';
import wolf65 from './assets/images/projects/wolf-65.jpeg';
import wolf67 from './assets/images/projects/wolf-67.jpeg';
import wolf68 from './assets/images/projects/wolf-68.jpeg';

// New project images
import kitchen1 from './assets/images/projects/Kitchen/kitchen-1.jpeg';
import kitchen2 from './assets/images/projects/Kitchen/kitchen-2.jpeg';
import kitchen3 from './assets/images/projects/Kitchen/kitchen-3.jpeg';

import pennvalley1 from './assets/images/projects/Penn Valley/pennvalley-1.jpeg';
import pennvalley2 from './assets/images/projects/Penn Valley/pennvalley-2.jpeg';
import pennvalley3 from './assets/images/projects/Penn Valley/pennvalley-3.jpeg';

import chromo1 from './assets/images/projects/chromo Colorado/chromo-1.jpeg';
import chromo2 from './assets/images/projects/chromo Colorado/chromo-2.jpeg';
import chromo5 from './assets/images/projects/chromo Colorado/chromo-5.jpeg';

import basement2_1 from './assets/images/projects/Basemant/basement2-1.jpeg';
import basement2_5 from './assets/images/projects/Basemant/basement2-5.jpeg';
import basement2_10 from './assets/images/projects/Basemant/basement2-10.jpeg';


// Custom hook for intersection observer
const useIntersectionObserver = (options = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting);
    }, options);

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [options]);

  return [ref, isIntersecting];
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { name: "Home", href: "#hero" },
    { name: "Services", href: "#services" },
    { name: "Portfolio", href: "#portfolio" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? "bg-white/95 backdrop-blur-md shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <a href="#hero" className="flex items-center">
            <img src={wolfLogo} alt="Wolf Solutions KC" className="h-10 w-10 mr-2 rounded-full" />
            <span
              className={`font-semibold text-lg transition-colors duration-200 ${
                isScrolled
                  ? "text-wolf-gray hover:text-steel-blue"
                  : "text-white hover:text-construction-red"
              }`}
            >
              Wolf Solutions
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className={`font-medium transition-colors duration-200 ${
                  isScrolled
                    ? "text-wolf-gray hover:text-steel-blue"
                    : "text-white hover:text-construction-red"
                }`}
              >
                {item.name}
              </a>
            ))}
            <a href="#contact" className="bg-construction-red text-white px-6 py-2 rounded-full font-semibold hover:bg-red-800 transition-colors duration-200">
              Get Quote
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`${isScrolled ? "text-wolf-gray" : "text-white"}`}
            >
              <i
                className={`fas ${
                  isMobileMenuOpen ? "fa-times" : "fa-bars"
                } text-xl`}
              ></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-md rounded-lg mt-2 p-4">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="block py-2 text-wolf-gray hover:text-steel-blue font-medium"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.name}
              </a>
            ))}
            <a href="#contact" onClick={() => setIsMobileMenuOpen(false)} className="block w-full mt-4 bg-construction-red text-white px-6 py-2 rounded-full font-semibold text-center">
              Get Quote
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const [heroRef, isHeroVisible] = useIntersectionObserver({ threshold: 0.1 });


  return (
    <section
      id="hero"
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
    >
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50 z-10"></div>
        <img
          src={heroImage}
          alt="Construction site"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className={`${isHeroVisible ? "animate-fade-in" : "opacity-0"}`}>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Building Your Ideas
            <span className="block text-construction-red">
              Realizing Your Dreams
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto">
            Over 15 years transforming spaces with excellence. Dedicated to your peace of mind on every project.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <a href="#contact" className="bg-construction-red text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-red-800 transition-all duration-300 hover:scale-105">
              Contact Us
            </a>
            <a href="#portfolio" className="glass-effect text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/20 transition-all duration-300">
              View Our Work
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
        <i className="fas fa-chevron-down text-2xl"></i>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const [servicesRef, isServicesVisible] = useIntersectionObserver({
    threshold: 0.1,
  });

  const services = [
    {
      icon: "fa-hammer",
      title: "Framing",
      description:
        "Professional structural framing services for residential and commercial projects.",
      features: ["Wood Framing", "Steel Framing", "Structural Support"],
    },
    {
      icon: "fa-paint-roller",
      title: "Stucco & Siding",
      description:
        "Expert exterior finishing with high-quality stucco and siding installation.",
      features: ["Stucco Application", "Vinyl Siding", "Fiber Cement"],
    },
    {
      icon: "fa-spray-can",
      title: "Insulation Spray Foam",
      description:
        "Energy-efficient spray foam insulation for superior thermal performance.",
      features: ["Spray Foam", "Energy Savings", "Air Sealing"],
    },
    {
      icon: "fa-th-large",
      title: "Drywall & Paint",
      description:
        "Complete drywall installation and professional painting services.",
      features: ["Drywall Install", "Texture Work", "Interior/Exterior Paint"],
    },
    {
      icon: "fa-layer-group",
      title: "Flooring",
      description:
        "Premium flooring installation including hardwood, tile, and luxury vinyl.",
      features: ["Hardwood", "Tile Installation", "Luxury Vinyl"],
    },
    {
      icon: "fa-home",
      title: "Roofing",
      description:
        "Complete roofing solutions from repairs to full roof replacements.",
      features: ["Roof Repair", "New Installation", "Storm Damage"],
    },
    {
      icon: "fa-utensils",
      title: "Custom Kitchens",
      description:
        "Beautiful custom kitchen design and installation with premium finishes.",
      features: ["Custom Cabinets", "Countertops", "Kitchen Islands"],
    },
    {
      icon: "fa-wrench",
      title: "Plumbing & Electrical",
      description:
        "Licensed plumbing and electrical services for all your construction needs.",
      features: ["Plumbing Install", "Electrical Wiring", "Fixture Install"],
    },
    {
      icon: "fa-cube",
      title: "Concrete",
      description:
        "Professional concrete work including foundations, driveways, and patios.",
      features: ["Foundations", "Driveways", "Decorative Concrete"],
    },
  ];

  return (
    <section id="services" ref={servicesRef} className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2
            className={`text-4xl md:text-5xl font-bold text-steel-blue mb-6 ${
              isServicesVisible ? "animate-fade-in" : "opacity-0"
            }`}
          >
            Complete Construction Solutions
          </h2>
          <p
            className={`text-xl text-medium-gray max-w-3xl mx-auto ${
              isServicesVisible ? "animate-fade-in" : "opacity-0"
            }`}
          >
            From start to finish, we deliver comprehensive construction services tailored to your needs and budget. Every project backed by our quality guarantee.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className={`bg-white rounded-xl shadow-lg p-8 hover-lift border border-gray-100 ${
                isServicesVisible ? "animate-slide-up" : "opacity-0"
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="text-4xl text-construction-red mb-4">
                <i className={`fas ${service.icon}`}></i>
              </div>
              <h3 className="text-xl font-bold text-steel-blue mb-3">
                {service.title}
              </h3>
              <p className="text-medium-gray mb-4">{service.description}</p>
              <ul className="space-y-2">
                {service.features.map((feature, idx) => (
                  <li
                    key={idx}
                    className="flex items-center text-sm text-medium-gray"
                  >
                    <i className="fas fa-check text-construction-red mr-2"></i>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Benefits Section
const BenefitsSection = () => {
  const [benefitsRef, isBenefitsVisible] = useIntersectionObserver({
    threshold: 0.1,
  });

  const benefits = [
    {
      icon: "fa-award",
      title: "Quality Guaranteed",
      description:
        "Every project backed by our comprehensive warranty. Your investment protected.",
    },
    {
      icon: "fa-clock",
      title: "On-Time, Every Time",
      description:
        "We respect your time. Projects completed as promised, no excuses.",
    },
    {
      icon: "fa-dollar-sign",
      title: "Honest Pricing",
      description:
        "Transparent quotes with no hidden costs. You know exactly what you're paying for.",
    },
    {
      icon: "fa-gem",
      title: "Premium Materials",
      description:
        "We only use top-quality materials from trusted suppliers for lasting results.",
    },
    {
      icon: "fa-users",
      title: "Experienced Team",
      description:
        "Skilled professionals with years of experience delivering exceptional results on every project.",
    },
    {
      icon: "fa-headset",
      title: "Direct Communication",
      description:
        "Always available. Quick responses and clear communication throughout your project.",
    },
  ];

  return (
    <section ref={benefitsRef} className="py-20 gradient-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2
            className={`text-4xl md:text-5xl font-bold text-white mb-6 ${
              isBenefitsVisible ? "animate-fade-in" : "opacity-0"
            }`}
          >
            Why Choose Wolf Solutions KC?
          </h2>
          <p
            className={`text-xl text-blue-100 max-w-3xl mx-auto ${
              isBenefitsVisible ? "animate-fade-in" : "opacity-0"
            }`}
          >
            We don't just build spaces, we build trust. Delivering peace of mind, guaranteed quality, and the exceptional value your family deserves.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className={`glass-effect rounded-xl p-8 text-center hover-lift ${
                isBenefitsVisible ? "animate-slide-up" : "opacity-0"
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="text-4xl text-construction-red mb-4">
                <i className={`fas ${benefit.icon}`}></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                {benefit.title}
              </h3>
              <p className="text-blue-100">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const [workRef, isWorkVisible] = useIntersectionObserver({ threshold: 0.1 });

  const steps = [
    {
      number: "01",
      title: "Free Consultation",
      description:
        "We meet to understand your vision, needs, and budget. No pressure, no obligation.",
      icon: "fa-comments",
    },
    {
      number: "02",
      title: "Detailed Quote",
      description:
        "We create clear plans and provide an honest, transparent quote you can understand.",
      icon: "fa-drafting-compass",
    },
    {
      number: "03",
      title: "Permits & Planning",
      description:
        "We handle all permits and coordination so you can focus on what matters most.",
      icon: "fa-clipboard-check",
    },
    {
      number: "04",
      title: "Expert Construction",
      description:
        "Skilled craftsmen bring your vision to life with regular updates and quality checks.",
      icon: "fa-hard-hat",
    },
    {
      number: "05",
      title: "Final Inspection",
      description:
        "Thorough quality inspection, final walkthrough, and handover with complete warranty documentation.",
      icon: "fa-key",
    },
  ];

  return (
    <section ref={workRef} className="py-20 bg-clean-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2
            className={`text-4xl md:text-5xl font-bold text-steel-blue mb-6 ${
              isWorkVisible ? "animate-fade-in" : "opacity-0"
            }`}
          >
            Our Simple Process
          </h2>
          <p
            className={`text-xl text-medium-gray max-w-3xl mx-auto ${
              isWorkVisible ? "animate-fade-in" : "opacity-0"
            }`}
          >
            Our proven 5-step approach ensures your project is completed on time, within budget, and exceeds your expectations.
          </p>
        </div>

        <div className="relative">
          {/* Progress line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gray-200 transform -translate-y-1/2"></div>
          <div
            className="hidden lg:block absolute top-1/2 left-0 h-1 bg-construction-red transform -translate-y-1/2 transition-all duration-1000"
            style={{ width: isWorkVisible ? "100%" : "0%" }}
          ></div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`text-center ${
                  isWorkVisible ? "animate-slide-up" : "opacity-0"
                }`}
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-construction-red rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <i className={`fas ${step.icon} text-2xl text-white`}></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-steel-blue rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {step.number}
                  </div>
                </div>
                <h3 className="text-lg font-bold text-steel-blue mb-3">
                  {step.title}
                </h3>
                <p className="text-medium-gray text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Portfolio Section
const PortfolioSection = () => {
  const [portfolioRef, isPortfolioVisible] = useIntersectionObserver({
    threshold: 0.1,
  });
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const projects = [
    {
      id: 1,
      title: "Basement Entertainment Room",
      category: "Basement Finishing",
      images: [wolf7, wolf2, wolf1, wolf4],
      description:
        "Complete basement transformation into a premium entertainment space featuring custom built-in cabinetry, large-screen TV installation, luxury vinyl plank flooring, and recessed lighting throughout.",
      details: {
        size: "1,200 sq ft",
        duration: "4 weeks",
        budget: "Contact for quote",
      },
    },
    {
      id: 2,
      title: "Custom Built-in Cabinetry",
      category: "Custom Millwork",
      images: [wolf8, wolf3, wolf6, wolf55],
      description:
        "Elegant custom built-in entertainment center with arched niches, floating wood shelves, shaker-style white cabinets, and premium brass hardware. Perfect for display and storage.",
      details: {
        size: "Wall unit",
        duration: "4 weeks",
        budget: "Contact for quote",
      },
    },
    {
      id: 3,
      title: "Luxury Bathroom Renovation",
      category: "Bathroom",
      images: [wolf9, wolf20, wolf15],
      description:
        "High-end bathroom featuring marble-look porcelain walls, frameless glass shower enclosure with brass hardware, modern vanity with quartz countertop, and designer sconce lighting.",
      details: {
        size: "Full bath",
        duration: "4 weeks",
        budget: "Contact for quote",
      },
    },
    {
      id: 4,
      title: "LVP Flooring Installation",
      category: "Flooring",
      images: [wolf65, wolf67, wolf68, wolf40],
      description:
        "Professional installation of premium luxury vinyl plank flooring throughout the basement. Water-resistant, durable, and featuring a natural wood-look finish perfect for below-grade spaces.",
      details: {
        size: "1,200 sq ft",
        duration: "4 week",
        budget: "Contact for quote",
      },
    },
    {
      id: 5,
      title: "Drywall & Paint",
      category: "Drywall",
      images: [wolf25, wolf30, wolf60, wolf35],
      description:
        "Complete drywall installation, taping, mudding, and professional painting. Includes custom arched openings, smooth finish throughout, and crown molding details.",
      details: {
        size: "1,200 sq ft",
        duration: "4 weeks",
        budget: "Contact for quote",
      },
    },
    {
      id: 6,
      title: "Barn Doors & Mirror Wall",
      category: "Custom Features",
      images: [wolf45, wolf5, wolf10, wolf11],
      description:
        "Custom barn door installation with premium brass sliding hardware, full-length mirror wall for home gym area, and decorative wall paneling with wainscoting details.",
      details: {
        size: "Multiple areas",
        duration: "5 weeks",
        budget: "Contact for quote",
      },
    },
    {
      id: 7,
      title: "Modern Loft Home",
      category: "New Construction",
      images: [kitchen1, kitchen2, kitchen3],
      description:
        "Complete loft-style home featuring open-concept kitchen with butcher block island, modern metal staircase with horizontal railings, LVP flooring, and farmhouse-style finishes throughout.",
      details: {
        size: "1,400 sq ft",
        duration: "5 weeks",
        budget: "Contact for quote",
      },
    },
    {
      id: 8,
      title: "Penn Valley New Build",
      category: "Framing & Insulation",
      images: [pennvalley1, pennvalley2, pennvalley3],
      description:
        "New construction project featuring complete framing, spray foam insulation, and drywall installation. High vaulted ceilings with loft area and open floor plan design.",
      details: {
        size: "2,200 sq ft",
        duration: "5 weeks",
        budget: "Contact for quote",
      },
    },
    {
      id: 9,
      title: "Chromo Colorado Home",
      category: "Drywall & Paint",
      images: [chromo1, chromo2, chromo5],
      description:
        "Stunning mountain home with cathedral ceilings, floor-to-ceiling stone fireplace, and complete drywall and painting work. Large windows throughout for natural light.",
      details: {
        size: "3,500 sq ft",
        duration: "4 weeks",
        budget: "Contact for quote",
      },
    },
    {
      id: 10,
      title: "Media Room & Kitchen",
      category: "Basement Finishing",
      images: [basement2_1, basement2_5, basement2_10],
      description:
        "Premium basement finish featuring media room with shiplap accent wall, built-in cabinets, surround sound, plus custom kitchen with quartz countertops and navy blue island.",
      details: {
        size: "1,800 sq ft",
        duration: "4 weeks",
        budget: "Contact for quote",
      },
    },
  ];

  // Image navigation functions
  const nextImage = () => {
    if (selectedProject) {
      setCurrentImageIndex((prev) =>
        prev === selectedProject.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedProject) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? selectedProject.images.length - 1 : prev - 1
      );
    }
  };

  // Reset image index when project changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [selectedProject]);

  return (
    <section id="portfolio" ref={portfolioRef} className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2
            className={`text-4xl md:text-5xl font-bold text-steel-blue mb-6 ${
              isPortfolioVisible ? "animate-fade-in" : "opacity-0"
            }`}
          >
            Our Work Speaks for Itself
          </h2>
          <p
            className={`text-xl text-medium-gray max-w-3xl mx-auto ${
              isPortfolioVisible ? "animate-fade-in" : "opacity-0"
            }`}
          >
            Real projects from real clients. See the quality and craftsmanship that sets us apart.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <div
              key={project.id}
              className={`group cursor-pointer ${
                isPortfolioVisible ? "animate-slide-up" : "opacity-0"
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
              onClick={() => setSelectedProject(project)}
            >
              <div className="relative overflow-hidden rounded-lg shadow-lg hover-lift">
                <img
                  src={project.images[0]}
                  alt={project.title}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="text-sm font-medium text-construction-red mb-1">
                    {project.category}
                  </div>
                  <h3 className="text-lg font-bold mb-2">{project.title}</h3>
                  <p className="text-sm">{project.description}</p>
                </div>
                {/* Image count indicator */}
                <div className="absolute top-4 right-4 bg-black/70 text-white px-2 py-1 rounded text-sm">
                  <i className="fas fa-images mr-1"></i>
                  {project.images.length}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Enhanced Project Modal with Image Gallery */}
        {selectedProject && (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedProject(null)}
          >
            <div
              className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Image Gallery Section */}
              <div className="relative">
                <img
                  src={selectedProject.images[currentImageIndex]}
                  alt={`${selectedProject.title} - ${
                    currentImageIndex + 1
                  }`}
                  className="w-full h-80 object-cover"
                />

                {/* Navigation Arrows */}
                {selectedProject.images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/70 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/90 transition-colors"
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/70 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/90 transition-colors"
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </>
                )}

                {/* Image Counter */}
                <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                  {currentImageIndex + 1} / {selectedProject.images.length}
                </div>

                {/* Close Button */}
                <button
                  onClick={() => setSelectedProject(null)}
                  className="absolute top-4 right-4 bg-white/90 rounded-full w-10 h-10 flex items-center justify-center hover:bg-white transition-colors"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              {/* Thumbnail Strip */}
              {selectedProject.images.length > 1 && (
                <div className="p-4 border-b">
                  <div className="flex space-x-2 overflow-x-auto">
                    {selectedProject.images.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                          index === currentImageIndex
                            ? "border-construction-red"
                            : "border-gray-200 hover:border-gray-400"
                        }`}
                      >
                        <img
                          src={image}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Project Details */}
              <div className="p-6">
                <div className="text-construction-red font-medium mb-2">
                  {selectedProject.category}
                </div>
                <h3 className="text-2xl font-bold text-steel-blue mb-4">
                  {selectedProject.title}
                </h3>
                <p className="text-medium-gray mb-6">
                  {selectedProject.description}
                </p>

                {/* Project Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center bg-clean-white rounded-lg p-4">
                    <div className="text-lg font-bold text-steel-blue">
                      {selectedProject.details.size}
                    </div>
                    <div className="text-sm text-medium-gray">Size</div>
                  </div>
                  <div className="text-center bg-clean-white rounded-lg p-4">
                    <div className="text-lg font-bold text-steel-blue">
                      {selectedProject.details.duration}
                    </div>
                    <div className="text-sm text-medium-gray">Duration</div>
                  </div>
                  <div className="text-center bg-clean-white rounded-lg p-4">
                    <div className="text-lg font-bold text-steel-blue">
                      {selectedProject.details.budget}
                    </div>
                    <div className="text-sm text-medium-gray">Investment</div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <a href="#contact" onClick={() => setSelectedProject(null)} className="flex-1 bg-construction-red text-white py-3 px-6 rounded-lg font-semibold hover:bg-red-800 transition-colors duration-200 text-center">
                    Get Similar Quote
                  </a>
                  <button onClick={() => setSelectedProject(null)} className="flex-1 bg-steel-blue text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200">
                    View More Projects
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// Testimonials Section
// const TestimonialsSection = () => {
//   const [testimonialsRef, isTestimonialsVisible] = useIntersectionObserver({
//     threshold: 0.1,
//   });
//   const [currentTestimonial, setCurrentTestimonial] = useState(0);

//   const testimonials = [
//     {
//       name: "Sarah Johnson",
//       role: "Homeowner",
//       image:
//         "https://images.unsplash.com/photo-1494790108755-2616b9e2b8b8?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
//       rating: 5,
//       text: "Wolf Solutions KC exceeded our expectations in every way. Their attention to detail and commitment to quality is unmatched. Our dream home became a reality thanks to their expertise.",
//       project: "Custom Home Build",
//     },
//     {
//       name: "Michael Chen",
//       role: "Business Owner",
//       image:
//         "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
//       rating: 5,
//       text: "Professional, reliable, and delivered on time. The office renovation transformed our workspace and improved our team's productivity. I wouldn't trust anyone else with my projects.",
//       project: "Office Renovation",
//     },
//     {
//       name: "Emily Rodriguez",
//       role: "Property Developer",
//       image:
//         "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
//       rating: 5,
//       text: "Working with Wolf Solutions KC on multiple projects has been fantastic. Their project management skills and quality workmanship make them our go-to construction partner.",
//       project: "Multi-Unit Development",
//     },
//   ];

//   useEffect(() => {
//     const interval = setInterval(() => {
//       setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
//     }, 5000);
//     return () => clearInterval(interval);
//   }, [testimonials.length]);

//   return (
//     <section ref={testimonialsRef} className="py-20 construction-gradient">
//       <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
//         <h2
//           className={`text-4xl md:text-5xl font-bold text-white mb-16 ${
//             isTestimonialsVisible ? "animate-fade-in" : "opacity-0"
//           }`}
//         >
//           What Our Clients Say
//         </h2>

//         <div
//           className={`relative ${
//             isTestimonialsVisible ? "animate-slide-up" : "opacity-0"
//           }`}
//         >
//           <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 md:p-12">
//             <div className="flex justify-center mb-6">
//               {[...Array(testimonials[currentTestimonial].rating)].map(
//                 (_, i) => (
//                   <i
//                     key={i}
//                     className="fas fa-star text-yellow-400 text-xl mx-1"
//                   ></i>
//                 )
//               )}
//             </div>

//             <blockquote className="text-xl md:text-2xl text-white mb-8 leading-relaxed">
//               "{testimonials[currentTestimonial].text}"
//             </blockquote>

//             <div className="flex items-center justify-center">
//               <img
//                 src={testimonials[currentTestimonial].image}
//                 alt={testimonials[currentTestimonial].name}
//                 className="w-16 h-16 rounded-full mr-4"
//               />
//               <div className="text-left">
//                 <div className="text-white font-bold text-lg">
//                   {testimonials[currentTestimonial].name}
//                 </div>
//                 <div className="text-orange-200">
//                   {testimonials[currentTestimonial].role}
//                 </div>
//                 <div className="text-orange-300 text-sm">
//                   {testimonials[currentTestimonial].project}
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Navigation dots */}
//           <div className="flex justify-center mt-8 space-x-2">
//             {testimonials.map((_, index) => (
//               <button
//                 key={index}
//                 onClick={() => setCurrentTestimonial(index)}
//                 className={`w-3 h-3 rounded-full transition-colors duration-200 ${
//                   index === currentTestimonial ? "bg-white" : "bg-white/50"
//                 }`}
//               />
//             ))}
//           </div>
//         </div>
//       </div>
//     </section>
//   );
// };

// FAQ Section
const FAQSection = () => {
  const [faqRef, isFAQVisible] = useIntersectionObserver({ threshold: 0.1 });
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqs = [
    {
      question: "How long does a typical construction project take?",
      answer:
        "Project timelines vary based on scope and complexity. Residential projects typically range from 3-12 months, while commercial projects can take 6-18 months. We provide detailed timelines during the planning phase and keep you updated throughout the process.",
    },
    {
      question: "What is included in your pricing estimates?",
      answer:
        "Our comprehensive quotes include materials, labor, permits, project management, and cleanup. We believe in transparent pricing with no hidden fees. Any changes to the original scope are discussed and approved before implementation.",
    },
    {
      question: "Are you licensed and insured?",
      answer:
        "Yes, Wolf Solutions KC is fully licensed, bonded, and insured. We carry comprehensive liability insurance and workers compensation coverage. All our contractors are certified professionals with proper licensing for their specialties.",
    },
    {
      question: "Do you source your own materials?",
      answer:
        "We work with trusted suppliers to source high-quality materials at competitive prices. We can also work with materials you provide, though we recommend using our vetted suppliers to ensure quality and warranty coverage.",
    },
    {
      question: "What warranty do you provide on your work?",
      answer:
        "We provide a comprehensive warranty on all our work - typically 1 year on labor and varying warranties on materials based on manufacturer specifications. We stand behind our craftsmanship and address any issues promptly.",
    },
    {
      question: "How do you handle project changes and cost overruns?",
      answer:
        "Any changes to the original scope require written approval and updated pricing. We maintain strict change order procedures to prevent unexpected costs. Our detailed planning phase minimizes the likelihood of overruns.",
    },
    {
      question: "What payment terms do you offer?",
      answer:
        "We typically work with a payment schedule tied to project milestones. A small deposit secures your project start date, with progress payments due at completion of major phases. Final payment is due upon project completion and your satisfaction.",
    },
    {
      question: "Do you handle permits and inspections?",
      answer:
        "Yes, we handle all necessary permits and coordinate required inspections. Our team is familiar with local building codes and regulations, ensuring your project meets all compliance requirements.",
    },
  ];

  return (
    <section ref={faqRef} className="py-20 bg-clean-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2
            className={`text-4xl md:text-5xl font-bold text-steel-blue mb-6 ${
              isFAQVisible ? "animate-fade-in" : "opacity-0"
            }`}
          >
            Frequently Asked Questions
          </h2>
          <p
            className={`text-xl text-medium-gray ${
              isFAQVisible ? "animate-fade-in" : "opacity-0"
            }`}
          >
            Get answers to common questions about our construction services and
            process.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`bg-white rounded-lg shadow-md overflow-hidden ${
                isFAQVisible ? "animate-slide-up" : "opacity-0"
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <button
                onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-clean-white transition-colors duration-200"
              >
                <span className="font-semibold text-steel-blue">
                  {faq.question}
                </span>
                <i
                  className={`fas fa-chevron-${
                    openFAQ === index ? "up" : "down"
                  } text-construction-red transition-transform duration-200`}
                ></i>
              </button>
              {openFAQ === index && (
                <div className="px-6 pb-4">
                  <p className="text-medium-gray leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [contactRef, isContactVisible] = useIntersectionObserver({
    threshold: 0.1,
  });

  const phoneNumber = "19132932454";
  const displayPhone = "(913) 293-2454";

  return (
    <section id="contact" ref={contactRef} className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2
            className={`text-4xl md:text-5xl font-bold text-steel-blue mb-6 ${
              isContactVisible ? "animate-fade-in" : "opacity-0"
            }`}
          >
            Let's Build Something Great Together
          </h2>
          <p
            className={`text-xl text-medium-gray max-w-3xl mx-auto ${
              isContactVisible ? "animate-fade-in" : "opacity-0"
            }`}
          >
            Get your free consultation today. Reach out directly and let's talk about your project.
          </p>
        </div>

        <div
          className={`max-w-4xl mx-auto ${
            isContactVisible ? "animate-slide-up" : "opacity-0"
          }`}
        >
          {/* Contact Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <a
              href={`tel:+${phoneNumber}`}
              className="flex items-center justify-center gap-4 bg-steel-blue text-white py-6 px-8 rounded-2xl font-semibold text-xl hover:bg-blue-700 transition-all duration-300 hover:scale-105 shadow-lg"
            >
              <i className="fas fa-phone text-2xl"></i>
              <span>Call {displayPhone}</span>
            </a>
            <a
              href="mailto:hectorvela077@gmail.com"
              className="flex items-center justify-center gap-4 bg-construction-red text-white py-6 px-8 rounded-2xl font-semibold text-xl hover:bg-red-800 transition-all duration-300 hover:scale-105 shadow-lg"
            >
              <i className="fas fa-envelope text-2xl"></i>
              <span>Send Email</span>
            </a>
          </div>

          {/* Contact Info Card */}
          <div className="bg-gradient-to-br from-steel-blue to-blue-800 rounded-2xl p-8 md:p-12 text-white shadow-xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-center">
              <div>
                <div className="bg-construction-red rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-clock text-2xl"></i>
                </div>
                <h4 className="font-semibold text-lg mb-2">Hours</h4>
                <p className="text-blue-100">
                  Available 24/7
                  <br />
                  Always here when you need us
                </p>
              </div>

              <div>
                <div className="bg-construction-red rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-map-marker-alt text-2xl"></i>
                </div>
                <h4 className="font-semibold text-lg mb-2">Service Area</h4>
                <p className="text-blue-100">
                  KCKS • KCMO
                  <br />
                  & Nationwide
                </p>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-blue-400 text-center">
              <p className="text-blue-100 text-lg">
                Contact us today for a free estimate on your next project!
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Section
const Footer = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="bg-wolf-gray text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center mb-4">
              <img src={wolfLogo} alt="Wolf Solutions KC" className="h-12 w-12 mr-3 rounded-full" />
              <span className="text-2xl font-bold text-construction-red">
                Wolf Solutions
              </span>
            </div>
            <p className="text-gray-300">
              Building your ideas, realizing your dreams. Your trusted partner for quality construction you can count on.
            </p>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Our Services</h3>
            <ul className="space-y-2 text-gray-300">
              <li>Basement Finishing</li>
              <li>Drywall & Paint</li>
              <li>Flooring Installation</li>
              <li>Custom Cabinetry</li>
              <li>Bathroom Remodeling</li>
              <li>Framing</li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <a
                  href="#services"
                  className="text-gray-300 hover:text-construction-red transition-colors duration-200"
                >
                  Services
                </a>
              </li>
              <li>
                <a
                  href="#portfolio"
                  className="text-gray-300 hover:text-construction-red transition-colors duration-200"
                >
                  Our Work
                </a>
              </li>
              <li>
                <a
                  href="#contact"
                  className="text-gray-300 hover:text-construction-red transition-colors duration-200"
                >
                  Get Quote
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Info</h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <i className="fas fa-map-marker-alt text-construction-red mr-3"></i>
                <span className="text-gray-300">
                  KCKS • KCMO
                  <br />
                  & Nationwide
                </span>
              </div>
              <div className="flex items-center">
                <i className="fas fa-phone text-construction-red mr-3"></i>
                <span className="text-gray-300">(913) 293-2454</span>
              </div>
              <div className="flex items-center">
                <i className="fas fa-envelope text-construction-red mr-3"></i>
                <span className="text-gray-300">hectorvela077@gmail.com</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-600 mt-12 pt-8 text-center">
          <div className="text-gray-400 text-sm">
            © {new Date().getFullYear()} Wolf Solutions KC. All rights reserved.
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-construction-red text-white p-3 rounded-full shadow-lg hover:bg-red-800 transition-all duration-300 hover:scale-110 z-40"
        >
          <i className="fas fa-chevron-up"></i>
        </button>
      )}
    </footer>
  );
};

export default function App() {
  return (
    <div className="App">
      <Navigation />
      <HeroSection />
      <ServicesSection />
      <BenefitsSection />
      <HowItWorksSection />
      <PortfolioSection />
      <FAQSection />
      <ContactSection />
      <Footer />
    </div>
  );
}
